import { call, put, takeLatest } from 'redux-saga/effects';

import { Api } from '../../../entries/ApiTransport';
import sagaAssessor from '../../../utils/sagaAssessor';
import { statisticTypes, statisticActions } from './actions';
import { getCachedData, setCachedData, getStatsCacheKey, clearStatsV3CacheForSalon } from '../../../utils/cacheManager';
import { getActiveSalonId } from 'utils/salonToken';
import { setStatsUnlock } from 'utils/statsUnlock';
import {
  getDemoBookings,
  getDemoClientsStats,
  getDemoFinance,
  getDemoQueueStats,
} from 'demo';
import { getLiveDemoBundle, selectIsDemoMode } from 'demo/saga';

const api = Api.getInstance();
const skipGlobalLoader = { skipLoader: true };

const buildQuery = (payload = {}) => {
  const params = new URLSearchParams();
  if (payload.period) params.set('period', payload.period);
  if (payload.compare) params.set('compare', payload.compare);
  if (payload.poltrone) params.set('poltrone', payload.poltrone);
  if (payload.clientiPeriod) params.set('clientiPeriod', payload.clientiPeriod);
  if (payload.dataInizio) params.set('dataInizio', payload.dataInizio);
  if (payload.dataFine) params.set('dataFine', payload.dataFine);
  if (payload.dataInizioPrev) params.set('dataInizioPrev', payload.dataInizioPrev);
  if (payload.dataFinePrev) params.set('dataFinePrev', payload.dataFinePrev);
  return params.toString();
};

const FRONTEND_STATS_PASSWORD = 'SuperBarba';

const getExpectedStatsUnlockPassword = () => {
  const fromEnv = String(process.env.REACT_APP_STATS_UNLOCK_PASSWORD || '').trim();
  return fromEnv || FRONTEND_STATS_PASSWORD;
};

const statsRequestConfig = () => ({
  timeout: 90000,
});

const hydrateFromCache = function* (successType, cacheKey) {
  const cached = getCachedData(cacheKey);
  if (cached) {
    yield put({
      type: successType,
      payload: cached,
      options: { fromCache: true },
    });
    return true;
  }
  // Nessuna cache: mantieni i dati del periodo precedente fino alla risposta API.
  return false;
};

const getDashboard = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const query = buildQuery(payload);
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          const finance = getDemoFinance(bundle, payload);
          const bookings = getDemoBookings(bundle, payload);
          const queue = getDemoQueueStats(payload);
          const clients = getDemoClientsStats(bundle);
          yield put(
            statisticActions.GET_DASHBOARD.SUCCESS({
              ...finance,
              ...bookings,
              ...queue,
              ...clients,
            }),
          );
          return;
        }
        const { data } = yield call(() =>
          api.get(`/business/statistiche/v3/dashboard${query ? `?${query}` : ''}`, {
            ...statsRequestConfig(),
          }),
        );
        yield put(statisticActions.GET_DASHBOARD.SUCCESS(data));
      },
    (err) => statisticActions.GET_DASHBOARD.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getStatisticsFinance = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const query = buildQuery(payload);
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(statisticActions.GET_STATISTICS_FINANCE.SUCCESS(getDemoFinance(bundle, payload)));
          return;
        }
        const cacheKey = getStatsCacheKey('finance', getActiveSalonId(), query);
        if (!options?.skipCache) {
          yield* hydrateFromCache(statisticTypes.GET_STATISTICS_FINANCE.SUCCESS, cacheKey);
        }

        if (options?.cacheOnly) return;

        const { data } = yield call(() =>
          api.get(`/business/statistiche/v3/finance${query ? `?${query}` : ''}`, {
            ...statsRequestConfig(),
          }),
        );
        setCachedData(cacheKey, data);
        yield put(statisticActions.GET_STATISTICS_FINANCE.SUCCESS(data));
      },
    (err) => statisticActions.GET_STATISTICS_FINANCE.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getStatisticsBookings = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const query = buildQuery(payload);
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(statisticActions.GET_STATISTICS_BOOKINGS.SUCCESS(getDemoBookings(bundle, payload)));
          return;
        }
        const cacheKey = getStatsCacheKey('bookings', getActiveSalonId(), query);
        if (!options?.skipCache) {
          yield* hydrateFromCache(statisticTypes.GET_STATISTICS_BOOKINGS.SUCCESS, cacheKey);
        }

        if (options?.cacheOnly) return;

        const { data } = yield call(() =>
          api.get(`/business/statistiche/v3/bookings${query ? `?${query}` : ''}`, {
            ...statsRequestConfig(),
          }),
        );
        setCachedData(cacheKey, data);
        yield put(statisticActions.GET_STATISTICS_BOOKINGS.SUCCESS(data));
      },
    (err) => statisticActions.GET_STATISTICS_BOOKINGS.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getStatisticsQueue = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const query = buildQuery(payload);
        if (yield* selectIsDemoMode()) {
          yield put(statisticActions.GET_STATISTICS_QUEUE.SUCCESS(getDemoQueueStats(payload)));
          return;
        }
        const cacheKey = getStatsCacheKey('queue', getActiveSalonId(), query);
        if (!options?.skipCache) {
          yield* hydrateFromCache(statisticTypes.GET_STATISTICS_QUEUE.SUCCESS, cacheKey);
        }

        if (options?.cacheOnly) return;

        const { data } = yield call(() =>
          api.get(`/business/statistiche/v3/queue${query ? `?${query}` : ''}`, {
            ...statsRequestConfig(),
          }),
        );
        setCachedData(cacheKey, data);
        yield put(statisticActions.GET_STATISTICS_QUEUE.SUCCESS(data));
      },
    (err) => statisticActions.GET_STATISTICS_QUEUE.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getStatisticsClients = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const query = buildQuery({
          period: payload?.period,
          poltrone: payload?.poltrone,
          dataInizio: payload?.dataInizio,
          dataFine: payload?.dataFine,
        });
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(statisticActions.GET_STATISTICS_CLIENTS.SUCCESS(getDemoClientsStats(bundle)));
          return;
        }
        const cacheKey = getStatsCacheKey('clients', getActiveSalonId(), query);
        if (!options?.skipCache) {
          yield* hydrateFromCache(statisticTypes.GET_STATISTICS_CLIENTS.SUCCESS, cacheKey);
        }

        if (options?.cacheOnly) return;

        const { data } = yield call(() =>
          api.get(`/business/statistiche/v3/clients${query ? `?${query}` : ''}`, {
            ...statsRequestConfig(),
          }),
        );
        setCachedData(cacheKey, data);
        yield put(statisticActions.GET_STATISTICS_CLIENTS.SUCCESS(data));
      },
    (err) => statisticActions.GET_STATISTICS_CLIENTS.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

function* unlockStatistics({ payload, callback }) {
  // Gate solo front: la password non passa dal backend.
  const password = String(payload?.password || '').trim();
  const expected = getExpectedStatsUnlockPassword();
  const notify = (err, data) => {
    if (typeof callback === 'function') callback(err, data);
  };

  if (expected && password === expected) {
    const salonId = getActiveSalonId();
    clearStatsV3CacheForSalon(salonId);
    setStatsUnlock(salonId, { fallback: true, submittedPassword: password });
    yield put(statisticActions.UNLOCK_STATISTICS.SUCCESS({ fallback: true }));
    notify(null, { fallback: true });
    return;
  }

  yield put(statisticActions.UNLOCK_STATISTICS.FAILED('WRONG_PASSWORD'));
  notify({ code: 'WRONG_PASSWORD' });
}

export default function* () {
  yield takeLatest(statisticTypes.GET_DASHBOARD.REQUEST, getDashboard);
  yield takeLatest(statisticTypes.GET_STATISTICS_FINANCE.REQUEST, getStatisticsFinance);
  yield takeLatest(statisticTypes.GET_STATISTICS_BOOKINGS.REQUEST, getStatisticsBookings);
  yield takeLatest(statisticTypes.GET_STATISTICS_QUEUE.REQUEST, getStatisticsQueue);
  yield takeLatest(statisticTypes.GET_STATISTICS_CLIENTS.REQUEST, getStatisticsClients);
  yield takeLatest(statisticTypes.UNLOCK_STATISTICS.REQUEST, unlockStatistics);
}
